import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, KeyRound, Crown } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { staffApi, errMsg, getUser } from "@/lib/api";
import { StaffPasscodeCard } from "@/components/staff/StaffPasscodeCard";

const EMPTY = { username: "", name: "", email: "", password: "", role: "admin", community_ids: [] };

const CommunityPick = ({ value, onChange }) => {
  const [list, setList] = useState([]);
  useEffect(() => { staffApi().get("/communities").then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <div className="flex flex-wrap items-center gap-1.5 sm:col-span-2 lg:col-span-6" data-testid="admin-new-communities">
      <span className="text-xs text-slate-500">Communities this leader can see:</span>
      {list.map((c) => {
        const on = value.includes(c.id);
        return <button key={c.id} type="button" onClick={() => onChange(on ? value.filter((x) => x !== c.id) : [...value, c.id])} data-testid={`admin-new-community-${c.slug}`}
          className={`rounded-full border px-3 py-1 text-xs ${on ? "border-[#0A3A6B] bg-[#0A3A6B] text-white" : "border-slate-200 text-slate-600"}`}>{c.name}</button>;
      })}
    </div>
  );
};

const AddAdminForm = ({ onAdded }) => {
  const [f, setF] = useState(EMPTY);
  const add = async (e) => {
    e.preventDefault();
    try { await staffApi().post("/admins", f); toast.success("Administrator added"); setF(EMPTY); onAdded(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={add} className="grid gap-3 rounded-3xl border border-slate-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.2fr_1fr_180px_auto]" data-testid="admin-add-form">
      <Input placeholder="Username" value={f.username} autoCapitalize="none" onChange={(e) => setF({ ...f, username: e.target.value })} data-testid="admin-new-username" />
      <Input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-new-name" />
      <Input type="email" placeholder="Email (optional)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-new-email" />
      <Input type="password" placeholder="Password (8+ chars)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-new-password" />
      <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
        <SelectTrigger data-testid="admin-new-role"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white"><SelectItem value="admin">Admin</SelectItem><SelectItem value="superadmin">Main admin</SelectItem><SelectItem value="leader">Community leader</SelectItem></SelectContent>
      </Select>
      {f.role === "leader" && <CommunityPick value={f.community_ids} onChange={(v) => setF({ ...f, community_ids: v })} />}
      <Button type="submit" className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="admin-add-button"><Plus className="mr-1 h-4 w-4" /> Add</Button>
    </form>
  );
};

export const AdminUsers = () => {
  const [list, setList] = useState([]);
  const me = getUser();
  const isSuper = me?.role === "superadmin";

  const load = useCallback(() => { staffApi().get("/admins").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const reset = async (a) => {
    const pw = window.prompt(`New password for ${a.username} (8+ characters)`);
    if (!pw) return;
    try { await staffApi().post(`/admins/${a.id}/reset-password`, { password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Remove administrator ${a.username}?`)) return;
    try { await staffApi().delete(`/admins/${a.id}`); toast.success("Administrator removed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Administrators</h2>
        <p className="text-sm text-slate-500">Only the main admin can add or remove administrators. There is no public sign-up.</p>
      </div>
      <StaffPasscodeCard isSuper={isSuper} />
      {isSuper ? <AddAdminForm onAdded={load} /> : <p className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">Only a main admin can change this list.</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {list.map((a) => (
          <div key={a.id} className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-row-${a.username}`}>
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#EBF3FA] font-display font-bold text-[#0A3A6B]">{a.name.slice(0, 1).toUpperCase()}</span>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 truncate font-medium text-slate-900">{a.name}{a.role === "superadmin" && <Crown className="h-3.5 w-3.5 shrink-0 text-amber-500" />}{a.role === "leader" && <span className="rounded-full bg-[#EBF3FA] px-2 py-0.5 text-[10px] font-semibold text-[#0A3A6B]" data-testid={`admin-leader-badge-${a.id}`}>Community leader</span>}</p>
              <p className="truncate text-sm text-slate-500">@{a.username}{a.email && ` · ${a.email}`}</p>
            </div>
            {isSuper && <Button size="icon" variant="ghost" onClick={() => reset(a)} className="shrink-0" title="Reset password" data-testid={`admin-reset-${a.username}`}><KeyRound className="h-4 w-4" /></Button>}
            {isSuper && a.id !== me.id && <Button size="icon" variant="ghost" onClick={() => remove(a)} className="shrink-0 text-rose-500" data-testid={`admin-delete-${a.username}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
      </div>
    </div>
  );
};
